import { authApi, isAuthError, type VerifyEmailRequest } from '@/api/login';
import EurodimaLogo from '@/components/assets/EurodimaLogo';
import { LoggedOutCard } from '@/components/layout/LoggedOutCard';
import { ConfirmAccountForm } from '@/features/register/ConfirmAccountForm';
import { MagicLoginForm } from '@/features/register/MagicLoginForm';
import { RegisterForm } from '@/features/register/RegisterForm';
import { useLoginRedirect } from '@/hooks/useLoginRedirect';
import { AUTH_QUERY_KEY, verifyAuth } from '@/lib/auth';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

type UseCase = 'register' | 'login';

interface AuthFlowCardProps {
  /** Determines which initial form and which verify endpoint to use */
  useCase: UseCase;
  /** When true, reads the ?redirect=<url> query parameter to redirect after successful verify */
  useNext?: boolean;
}

/**
 * Two-step auth flow card that slides between an initial form and a confirmation view.
 * The initial form is swapped based on `useCase`:
 *  - register → RegisterForm + verifyEmail endpoint
 *  - login    → MagicLoginForm + loginWithToken endpoint
 */
export function AuthFlowCard({ useCase, useNext = false }: AuthFlowCardProps) {
  const [currentView, setCurrentView] = useState<'initial' | 'confirm'>('initial');
  const [email, setEmail] = useState('');
  const [verifyError, setVerifyError] = useState<string | null>(null);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { followRedirect } = useLoginRedirect();

  const handleInitialSuccess = (submittedEmail: string) => {
    setEmail(submittedEmail);
    setCurrentView('confirm');
  };

  const waitForAuthenticatedUser = async () => {
    await queryClient.invalidateQueries({ queryKey: AUTH_QUERY_KEY });

    for (let attempt = 0; attempt < 5; attempt += 1) {
      const user = await queryClient.fetchQuery({
        queryKey: AUTH_QUERY_KEY,
        queryFn: verifyAuth,
        staleTime: 0,
      });

      if (user) return true;

      await new Promise((resolve) => setTimeout(resolve, 1000));
    }

    return false;
  };

  const handleConfirm = async (code: string) => {
    setVerifyError(null);
    try {
      const verifyData: VerifyEmailRequest = { email, token: code };

      if (useCase === 'register') {
        await authApi.verifyEmail(verifyData);
      } else {
        await authApi.loginWithToken(verifyData);
      }

      const didLoadUser = await waitForAuthenticatedUser();

      if (!didLoadUser) {
        setVerifyError(t('auth.verificationFailed', 'Verification failed. Please try again.'));
        return;
      }

      if (useNext) {
        followRedirect();
        return;
      }
      navigate({ to: '/' });
    } catch (err) {
      if (isAuthError(err)) {
        setVerifyError(err.message);
      } else {
        setVerifyError(t('auth.verificationFailed', 'Verification failed. Please try again.'));
      }
    }
  };

  const minHeightByUseCase = { register: 'min-h-[420px]', login: 'min-h-[220px]' } as const;

  return (
    <LoggedOutCard>
      <div className="flex justify-center mb-8">
        <div className="w-48">
          <EurodimaLogo />
        </div>
      </div>

      <div className={`relative ${minHeightByUseCase[useCase]} h-fit`}>
        <div className={`relative inset-0 h-fit transition-all duration-500 ease-in-out ${currentView === 'initial'
          ? 'translate-x-0 opacity-100'
          : '-translate-x-full opacity-0 pointer-events-none'
          }`}>
          {useCase === 'register'
            ? <RegisterForm onSuccess={handleInitialSuccess} />
            : <MagicLoginForm onSuccess={handleInitialSuccess} />
          }
        </div>

        <div className={`absolute inset-0 transition-all duration-500 ease-in-out ${currentView === 'confirm'
          ? 'translate-x-0 opacity-100'
          : 'translate-x-full opacity-0 pointer-events-none'
          }`}>
          <ConfirmAccountForm
            onSubmit={handleConfirm}
            isActive={currentView === 'confirm'}
            error={verifyError}
            onClearError={() => setVerifyError(null)}
          />
        </div>
      </div>
    </LoggedOutCard>
  );
}
